// frontend/src/pages/SurgeryRequestCsvHelp.js
// /surgery-request の CSV 取込ヘルプページ
// 別タブで開く想定。SurgeryRequest.js から window.open で呼ばれる。

import React from 'react';

const SurgeryRequestCsvHelp = () => {
  const sectionStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '1px solid #e5e7eb',
  };
  const h2Style = {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1e40af',
    marginTop: 0,
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid #dbeafe',
  };
  const h3Style = {
    fontSize: '15px',
    fontWeight: 700,
    color: '#0f172a',
    marginTop: '16px',
    marginBottom: '8px',
  };
  const codeStyle = {
    background: '#f1f5f9',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: 'Menlo, Consolas, monospace',
    color: '#0f172a',
  };
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    marginTop: '8px',
  };
  const thStyle = {
    background: '#eff6ff',
    padding: '8px 12px',
    textAlign: 'left',
    fontWeight: 700,
    color: '#1e40af',
    border: '1px solid #bfdbfe',
  };
  const tdStyle = {
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    verticalAlign: 'top',
  };
  const badgeRequired = {
    display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
    background: '#fee2e2', color: '#dc2626', fontSize: '11px', fontWeight: 700,
  };
  const badgeOptional = {
    display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
    background: '#dbeafe', color: '#1e40af', fontSize: '11px', fontWeight: 700,
  };
  const calloutStyle = {
    background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px',
    padding: '12px 16px', marginTop: '12px', color: '#78350f', fontSize: '13px',
  };
  const calloutInfoStyle = {
    background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '8px',
    padding: '12px 16px', marginTop: '12px', color: '#1e40af', fontSize: '13px',
  };
  const calloutErrorStyle = {
    background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px',
    padding: '12px 16px', marginTop: '12px', color: '#7f1d1d', fontSize: '13px',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc, #eff6ff)',
      padding: '24px',
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* ヘッダー */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{
            fontSize: '28px', fontWeight: 800, color: '#0f172a',
            margin: 0, marginBottom: '8px',
          }}>
            📋 手術予定 CSV 取込マニュアル
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            手術予定 CSV のインポート仕様・形式・トラブルシューティングをまとめたマニュアルです。
          </p>
        </div>

        {/* 1. 概要 */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>1. 概要</h2>
          <p>
            手術予定一覧を Excel / Google Sheets / その他表計算ソフトで作成し、
            CSV 形式に書き出して ScrubEdge にインポートできます。
          </p>
          <p>取込時は次のように動作します:</p>
          <ul>
            <li>1 行 = 1 手術として登録</li>
            <li>既に同じ <code style={codeStyle}>(日付, 部屋, 開始時刻)</code> の手術がある場合は重複としてスキップ</li>
            <li>部屋名が ScrubEdge に未登録の場合は警告表示（オプションで取込中止可）</li>
            <li>必須カラム以外は空欄でも問題なし</li>
          </ul>
        </div>

        {/* 2. ファイル形式 */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>2. ファイル形式・文字コード</h2>
          <h3 style={h3Style}>📄 ファイル形式</h3>
          <ul>
            <li>拡張子: <code style={codeStyle}>.csv</code></li>
            <li>区切り文字: カンマ ( <code style={codeStyle}>,</code> )</li>
            <li>1 行目: ヘッダー (カラム名)</li>
            <li>2 行目以降: データ行</li>
            <li>値にカンマを含む場合はダブルクォートで囲む: <code style={codeStyle}>"○○, △△"</code></li>
          </ul>

          <h3 style={h3Style}>🈂️ 文字コード</h3>
          <ul>
            <li>
              <strong>推奨: UTF-8 (BOM 付き)</strong>
              <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '13px' }}>
                — Excel で開いても文字化けしない
              </span>
            </li>
            <li>Shift-JIS でも取込み自体は可能だが、Excel 経由での編集ループで文字化けする可能性あり</li>
            <li>テンプレートダウンロードで取得した CSV は UTF-8 (BOM 付き) で出力されます</li>
          </ul>
          <div style={calloutStyle}>
            💡 <strong>Excel で編集・保存する場合の注意</strong><br />
            「名前を付けて保存」→ ファイル形式に「CSV UTF-8 (コンマ区切り)」を選択してください。
            単に「CSV (コンマ区切り)」だと Shift-JIS で保存され、漢字が化ける可能性があります。
          </div>

          <h3 style={h3Style}>💬 コメント行</h3>
          <p>
            行頭が <code style={codeStyle}>#</code> で始まる行は<strong>コメント行</strong>として無視されます。
            自分用のメモを書きたい場合に使えます。
          </p>
        </div>

        {/* 3. カラム一覧 */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>3. カラム一覧</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>カラム名</th>
                <th style={thStyle}>必須</th>
                <th style={thStyle}>形式</th>
                <th style={thStyle}>説明</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>room</code></td>
                <td style={tdStyle}><span style={badgeRequired}>必須</span></td>
                <td style={tdStyle}>固定</td>
                <td style={tdStyle}>
                  部屋名。<strong>手術室設定の表記と完全一致</strong>させること。
                  例: <code style={codeStyle}>OR1</code>, <code style={codeStyle}>OR2</code>
                </td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>start</code></td>
                <td style={tdStyle}><span style={badgeRequired}>必須</span></td>
                <td style={tdStyle}>固定</td>
                <td style={tdStyle}>
                  開始時刻。<code style={codeStyle}>HH:MM</code> 形式。例: <code style={codeStyle}>09:00</code>
                  <br />TF モードでは <code style={codeStyle}>TF1</code>, <code style={codeStyle}>TF2</code>… も可。
                </td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>end</code></td>
                <td style={tdStyle}><span style={badgeRequired}>必須*</span></td>
                <td style={tdStyle}>固定</td>
                <td style={tdStyle}>
                  終了時刻。<code style={codeStyle}>HH:MM</code> 形式。<br />
                  <small>* 通常モードのみ必須 (TF モードでは duration を使用)</small>
                </td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>duration</code></td>
                <td style={tdStyle}><span style={badgeRequired}>必須*</span></td>
                <td style={tdStyle}>柔軟</td>
                <td style={tdStyle}>
                  占有時間。<code style={codeStyle}>2:30</code> / <code style={codeStyle}>150</code> (分) /{' '}
                  <code style={codeStyle}>1時間30分</code> / <code style={codeStyle}>1h30m</code> 等。<br />
                  <small>* TF モードのみ必須</small>
                </td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>procedure</code></td>
                <td style={tdStyle}><span style={badgeOptional}>任意</span></td>
                <td style={tdStyle}>フリーテキスト</td>
                <td style={tdStyle}>術式名。空欄可。<br />術式マスタにあれば紐づけ、なければそのまま保存。</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>department</code></td>
                <td style={tdStyle}><span style={badgeOptional}>任意</span></td>
                <td style={tdStyle}>フリーテキスト</td>
                <td style={tdStyle}>診療科。例: 心臓血管外科、消化器外科。</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>surgeon</code></td>
                <td style={tdStyle}><span style={badgeOptional}>任意</span></td>
                <td style={tdStyle}>フリーテキスト</td>
                <td style={tdStyle}>執刀医名。フリーテキスト。空欄・列削除も可。</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>priority</code></td>
                <td style={tdStyle}><span style={badgeOptional}>任意</span></td>
                <td style={tdStyle}>マッピング</td>
                <td style={tdStyle}>
                  優先度。下記マッピング参照。空欄なら <code style={codeStyle}>normal</code>。
                </td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>position</code></td>
                <td style={tdStyle}><span style={badgeOptional}>任意</span></td>
                <td style={tdStyle}>フリーテキスト</td>
                <td style={tdStyle}>体位。例: 仰臥位、腹臥位、側臥位。空欄・列削除も可。</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>anesthesia</code></td>
                <td style={tdStyle}><span style={badgeOptional}>任意</span></td>
                <td style={tdStyle}>フリーテキスト</td>
                <td style={tdStyle}>麻酔種別。例: 全身麻酔、硬膜外麻酔。</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>requiredSkills</code></td>
                <td style={tdStyle}><span style={badgeOptional}>任意</span></td>
                <td style={tdStyle}>カンマ区切り</td>
                <td style={tdStyle}>
                  必要スキル名のリスト。複数指定時はカンマ区切り。
                  <br />例: <code style={codeStyle}>"冠動脈バイパス術,体外循環"</code>
                </td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>skillCondition</code></td>
                <td style={tdStyle}><span style={badgeOptional}>任意</span></td>
                <td style={tdStyle}>固定</td>
                <td style={tdStyle}>
                  <code style={codeStyle}>AND</code> = すべて必要 / <code style={codeStyle}>OR</code> = いずれか
                  空欄なら <code style={codeStyle}>AND</code> 扱い。
                </td>
              </tr>
            </tbody>
          </table>
          <div style={calloutInfoStyle}>
            ℹ️ <strong>列の順序は自由</strong>。ヘッダー名で照合するので、カラムの並び順を変えても問題ありません。
            <br />
            ℹ️ <strong>任意カラムは列ごと削除も可</strong>。CSV にカラムが存在しなければ無視されます。
          </div>
        </div>

        {/* 4. 部屋名 */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>4. 部屋名 (room) — 完全一致が必要</h2>
          <p>
            部屋名は <strong>ScrubEdge の「手術室設定」で登録した表記と完全一致</strong>している必要があります。
            大文字・小文字も区別されます。
          </p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>SE 側設定</th>
                <th style={thStyle}>CSV の値</th>
                <th style={thStyle}>結果</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>OR1</code></td>
                <td style={tdStyle}><code style={codeStyle}>OR1</code></td>
                <td style={tdStyle} >✅ 取込まれる</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>OR1</code></td>
                <td style={tdStyle}><code style={codeStyle}>OR01</code></td>
                <td style={tdStyle}>❌ 取込まれない (ゼロ埋め違い)</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>OR1</code></td>
                <td style={tdStyle}><code style={codeStyle}>or1</code></td>
                <td style={tdStyle}>❌ 取込まれない (大文字小文字違い)</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>OR1</code></td>
                <td style={tdStyle}><code style={codeStyle}>Op1</code></td>
                <td style={tdStyle}>❌ 取込まれない (略称違い)</td>
              </tr>
              <tr>
                <td style={tdStyle}><code style={codeStyle}>OR1</code></td>
                <td style={tdStyle}><code style={codeStyle}>OR 1</code></td>
                <td style={tdStyle}>❌ 取込まれない (空白あり)</td>
              </tr>
            </tbody>
          </table>
          <div style={calloutStyle}>
            💡 <strong>「テンプレートダウンロード」</strong>から取得する CSV には、
            自院に登録済みの部屋一覧が全て含まれているので、それを参考にしてください。
          </div>
          <div style={calloutErrorStyle}>
            ⚠️ <strong>取込時に未登録部屋を検出すると警告表示</strong>されます。
            取込を中止して部屋設定を見直すか、CSV 側を修正してください。
          </div>
        </div>

        {/* 5. 時刻 */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>5. 時刻 (start / end / duration)</h2>
          <h3 style={h3Style}>✅ 認識される形式</h3>
          <ul>
            <li><code style={codeStyle}>09:00</code> / <code style={codeStyle}>9:00</code> (HH:MM)</li>
            <li><code style={codeStyle}>0900</code> / <code style={codeStyle}>900</code> (4 桁 or 3 桁)</li>
            <li>TF モードのみ: <code style={codeStyle}>TF1</code>, <code style={codeStyle}>TF2</code>, <code style={codeStyle}>TF3</code>…</li>
          </ul>
          <h3 style={h3Style}>❌ 認識されない形式</h3>
          <ul>
            <li><code style={codeStyle}>9時00分</code> (漢字混在)</li>
            <li><code style={codeStyle}>9時30分</code></li>
            <li><code style={codeStyle}>Oncall</code> (オンコールは時刻と認識されない)</li>
            <li><code style={codeStyle}>AM9:00</code></li>
          </ul>
          <h3 style={h3Style}>⏳ duration の形式 (TF モード)</h3>
          <ul>
            <li><code style={codeStyle}>2:30</code> (2 時間 30 分)</li>
            <li><code style={codeStyle}>150</code> (150 分 = 2 時間 30 分)</li>
            <li><code style={codeStyle}>1時間30分</code></li>
            <li><code style={codeStyle}>1h30m</code></li>
            <li><code style={codeStyle}>2時間</code> (時間のみも OK)</li>
          </ul>
          <div style={calloutInfoStyle}>
            ℹ️ TF (TimeFrame) モードは「前の手術が終わり次第開始」を表します。
            時刻ではなく <code style={codeStyle}>TF1</code> 等の枠を指定し、duration で時間を確保します。
          </div>
        </div>

        {/* 6. 優先度 */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>6. 優先度 (priority) のマッピング</h2>
          <p>下記キーワードを<strong>部分一致</strong>で判定します。</p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>CSV の値</th>
                <th style={thStyle}>判定結果</th>
                <th style={thStyle}>表示</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>
                  <code style={codeStyle}>normal</code> / <code style={codeStyle}>通常</code> /{' '}
                  <code style={codeStyle}>定時</code> / (空欄)
                </td>
                <td style={tdStyle}><code style={codeStyle}>normal</code></td>
                <td style={tdStyle}><span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '4px' }}>通常 (緑)</span></td>
              </tr>
              <tr>
                <td style={tdStyle}>
                  <code style={codeStyle}>urgent</code> / <code style={codeStyle}>優先</code>
                </td>
                <td style={tdStyle}><code style={codeStyle}>urgent</code></td>
                <td style={tdStyle}><span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '4px' }}>優先 (黄)</span></td>
              </tr>
              <tr>
                <td style={tdStyle}>
                  <code style={codeStyle}>emergency</code> / <code style={codeStyle}>緊急</code> /{' '}
                  <code style={codeStyle}>超緊急</code>
                </td>
                <td style={tdStyle}><code style={codeStyle}>emergency</code></td>
                <td style={tdStyle}><span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px' }}>超緊急 (赤)</span></td>
              </tr>
            </tbody>
          </table>
          <div style={calloutStyle}>
            💡 <strong>「緊急」と入力すると「超緊急」 (赤) になります</strong>。
            通常の優先度ランクアップを意図する場合は <code style={codeStyle}>urgent</code> または{' '}
            <code style={codeStyle}>優先</code> を使用してください。
          </div>
        </div>

        {/* 7. モード切替 */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>7. 通常モード vs TF モード</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}></th>
                <th style={thStyle}>通常モード</th>
                <th style={thStyle}>TF モード</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>開始時刻 (start)</td>
                <td style={tdStyle}><code style={codeStyle}>HH:MM</code> 必須</td>
                <td style={tdStyle}><code style={codeStyle}>HH:MM</code> or <code style={codeStyle}>TFn</code></td>
              </tr>
              <tr>
                <td style={tdStyle}>終了時刻 (end)</td>
                <td style={tdStyle}><strong>必須</strong></td>
                <td style={tdStyle}>不要</td>
              </tr>
              <tr>
                <td style={tdStyle}>占有時間 (duration)</td>
                <td style={tdStyle}>不要 (end から自動計算)</td>
                <td style={tdStyle}><strong>必須</strong></td>
              </tr>
              <tr>
                <td style={tdStyle}>用途</td>
                <td style={tdStyle}>確定スケジュール</td>
                <td style={tdStyle}>枠予約 (順次実施)</td>
              </tr>
            </tbody>
          </table>
          <p style={{ marginTop: '12px' }}>
            モード切替は <strong>「CSV取込メニュー → 設定」</strong> から行えます。
            テンプレートダウンロードは現在のモードに応じた形式で出力されます。
          </p>
        </div>

        {/* 8. よくあるエラー */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>8. よくあるエラーと対処</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>症状</th>
                <th style={thStyle}>原因</th>
                <th style={thStyle}>対処</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>取込後に何も表示されない</td>
                <td style={tdStyle}>部屋名が未登録</td>
                <td style={tdStyle}>テンプレートで部屋名を確認・修正</td>
              </tr>
              <tr>
                <td style={tdStyle}>一部の手術だけ取込まれない</td>
                <td style={tdStyle}>start が <code style={codeStyle}>HH:MM</code> 形式でない</td>
                <td style={tdStyle}>「9:00」など固定形式に修正</td>
              </tr>
              <tr>
                <td style={tdStyle}>緊急扱いになってしまう</td>
                <td style={tdStyle}>「緊急」が「超緊急」と判定されている</td>
                <td style={tdStyle}>通常レベルなら <code style={codeStyle}>urgent</code> or <code style={codeStyle}>優先</code></td>
              </tr>
              <tr>
                <td style={tdStyle}>漢字が文字化けする</td>
                <td style={tdStyle}>Shift-JIS で保存されている</td>
                <td style={tdStyle}>Excel で「CSV UTF-8」として保存し直す</td>
              </tr>
              <tr>
                <td style={tdStyle}>TF1 が 9:00 になる</td>
                <td style={tdStyle}>その部屋で前手術がない</td>
                <td style={tdStyle}>09:00 (固定時刻) と明示指定する</td>
              </tr>
              <tr>
                <td style={tdStyle}>同じ手術が重複しない</td>
                <td style={tdStyle}>(日付, 部屋, 開始時刻) が一致 → スキップされている</td>
                <td style={tdStyle}>意図的に異なる時刻にする or 既存をクリア</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 9. 取込手順 */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>9. 取込手順</h2>
          <ol>
            <li>「CSV取込メニュー」→ <strong>テンプレートダウンロード</strong> で空のテンプレを取得</li>
            <li>Excel / 表計算ソフトで開いて手術予定を入力</li>
            <li>「名前を付けて保存」→ <strong>CSV UTF-8 (コンマ区切り)</strong> を選択</li>
            <li>「CSV取込メニュー」→ <strong>ファイル選択</strong> → アップロード</li>
            <li>取込結果のプレビューを確認</li>
            <li>未登録部屋などの警告があれば対処</li>
            <li>確定で登録</li>
          </ol>
        </div>

        {/* 戻るボタン */}
        <div style={{ textAlign: 'center', marginTop: '32px', marginBottom: '32px' }}>
          <button
            onClick={() => window.close()}
            style={{
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              color: 'white', border: 'none', borderRadius: '8px',
              fontWeight: 700, cursor: 'pointer', fontSize: '14px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            このタブを閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurgeryRequestCsvHelp;
